import React from 'react';
import { Alert, Typography, Box } from '@mui/material';
import { Error as ErrorIcon } from '@mui/icons-material';

export interface FormValidationAlertProps {
  /**
   * Objeto com erros de validação (chave: nome do campo, valor: mensagem de erro)
   */
  errors: Record<string, string> | Partial<Record<string, string>>;
  
  /**
   * Mensagem de erro geral do servidor (opcional)
   */
  serverError?: string | null;
  
  /**
   * Se deve fazer scroll para o topo quando há erros
   */
  scrollOnError?: boolean;
  
  /**
   * Classe CSS adicional
   */
  className?: string;
  
  /**
   * Estilos adicionais
   */
  sx?: object;
}

/**
 * Componente centralizado para exibir alertas de validação de formulários
 * 
 * Exibe:
 * - Mensagem de erro do servidor (se houver)
 * - Mensagem informando quantos campos obrigatórios não foram preenchidos
 * - Instruções para verificar os campos destacados em vermelho
 * 
 * @example
 * ```tsx
 * const { errors } = useForm();
 * 
 * <FormValidationAlert 
 *   errors={errors}
 *   serverError={apiError}
 *   scrollOnError={true}
 * />
 * ```
 */
export const FormValidationAlert: React.FC<FormValidationAlertProps> = ({
  errors,
  serverError,
  scrollOnError = true,
  className,
  sx,
}) => {
  // Contar quantos campos têm erro
  const errorCount = Object.keys(errors).filter(
    key => {
      const error = errors[key];
      return error && typeof error === 'string' && error.trim() !== '';
    }
  ).length;

  const hasErrors = errorCount > 0 || (serverError && serverError.trim() !== '');

  // Fazer scroll para o topo quando há erros (apenas uma vez)
  React.useEffect(() => {
    if (hasErrors && scrollOnError) {
      // Pequeno delay para garantir que o componente foi renderizado
      const timer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasErrors, scrollOnError]);

  // Se não há erros, não renderiza nada
  if (!hasErrors) {
    return null;
  }

  return (
    <Box className={className} sx={{ mb: 2, ...sx }}>
      {/* Erro do servidor (prioridade) */}
      {serverError && serverError.trim() !== '' && (
        <Alert severity="error" sx={{ mb: errorCount > 0 ? 2 : 0 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Erro ao processar formulário
          </Typography>
          <Typography variant="body2">
            {serverError}
          </Typography>
        </Alert>
      )}

      {/* Erros de validação de campos */}
      {errorCount > 0 && (
        <Alert 
          severity="error" 
          icon={<ErrorIcon />}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Por favor, preencha todos os campos obrigatórios
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {errorCount === 1
              ? 'Há 1 campo obrigatório não preenchido ou inválido. Verifique os campos destacados em vermelho abaixo.'
              : `Há ${errorCount} campos obrigatórios não preenchidos ou inválidos. Verifique os campos destacados em vermelho abaixo.`
            }
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {Object.entries(errors).map(([key, message]) =>
              message && typeof message === 'string' && message.trim() !== '' ? (
                <Typography key={key} component="li" variant="body2" sx={{ mb: 0.5 }}>
                  {message}
                </Typography>
              ) : null
            )}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default FormValidationAlert;

